import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useRef, useState } from 'react'
import { repository } from '../../data'
import { prepareImage } from '../imageFile'
import { Glyph } from './glyphs'
import { errorText } from './shared'

/**
 * "Add photo" for an item — one tap opens the phone's rear camera
 * (`capture="environment"`); a desktop ignores that and opens a file picker,
 * which is the right answer there.
 *
 * The photo is resized on the device (`prepareImage`, ≤1400px JPEG) and filed
 * against the item's type through the same attachments store every other photo
 * uses, so it is the picture on the tile, the item page, Receive and Design.
 */
export function AddPhotoButton({
  itemId,
  label = 'Add photo',
  primary = false,
  className = '',
  onAdded,
}: {
  itemId: string
  label?: string
  primary?: boolean
  className?: string
  onAdded?: () => void
}): JSX.Element {
  const client = useQueryClient()
  const input = useRef<HTMLInputElement>(null)
  const [problem, setProblem] = useState<string | null>(null)

  const upload = useMutation({
    mutationFn: async (files: readonly File[]) => {
      for (const file of files) {
        const prepared = await prepareImage(file)
        if (!prepared.ok) throw new Error(prepared.reason)
        URL.revokeObjectURL(prepared.image.previewUrl)
        await repository.uploadAttachment({ kind: 'component_type', id: itemId }, prepared.image.blob, {
          width: prepared.image.width,
          height: prepared.image.height,
        })
      }
    },
    onSuccess: () => onAdded?.(),
    onError: (e) => setProblem(errorText(e)),
    onSettled: async () => {
      // Everywhere this item's picture is drawn.
      for (const key of [
        ['attachments', 'component_type', itemId],
        ['inventory'],
        ['inventory-item', itemId],
        ['receipts'],
        ['stock'],
        ['component-types'],
      ]) {
        await client.invalidateQueries({ queryKey: key })
      }
    },
  })

  return (
    <>
      <input
        ref={input}
        type="file"
        accept="image/*"
        capture="environment"
        hidden
        onChange={(e) => {
          setProblem(null)
          const files = [...(e.target.files ?? [])]
          e.target.value = ''
          if (files.length > 0) upload.mutate(files)
        }}
      />
      <button
        type="button"
        className={`${primary ? 'primary ' : ''}${className}`}
        onClick={() => input.current?.click()}
        disabled={upload.isPending}
      >
        <Glyph name="camera" />
        {upload.isPending ? 'Adding photo…' : label}
      </button>
      {problem ? <p className="critical inv-quiet">{problem}</p> : null}
    </>
  )
}
